import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle,
  RefreshCw, ChevronDown, ChevronUp, Loader2, Building2, Shield,
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { renewalDeepLink } from '../utils/deepLinks';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExpirationItem {
  documentId: string;
  intelligenceId: string;
  customerId: string | null;
  customerName: string | null;
  projectId: string | null;
  namedInsured: string | null;
  lineOfBusiness: string | null;
  carrier: string | null;
  expirationDate: string;
  effectiveDate: string | null;
  totalPremium: number | null;
  confidence: number | null;
  daysOut: number;
  bucket: 'expired' | '0-30' | '31-60' | '61-90' | '91+';
}

interface ExpirationsResponse {
  buckets: Record<string, ExpirationItem[]>;
  items: ExpirationItem[];
}

interface BookTrend {
  lineOfBusiness: string;
  avgChangePct: number | null;
  termCount: number;
}

// The endpoint also returns `chains`, which this page doesn't use — omit it
// from the type so it never gets stored in state.
interface BookTrendsResponse {
  bookSummary: BookTrend[];
}

interface GapItem {
  customerId: string | null;
  namedInsured: string | null;
  presentLines: string[];
  missingLines: string[];
  isMonoline: boolean;
  accountPremium: number | null;
}

interface GapsResponse {
  gaps: GapItem[];
  totalMatches: number;
}

interface MarketSignal {
  carrier: string | null;
  lineOfBusiness: string | null;
  signalType: string;
  count: number;
  dateRange: { earliest: string; latest: string };
  quotes: string[];
}

interface CarrierRetention {
  carrier: string;
  renewed: number;
  moved: number;
  lapsed: number;
  retentionRate: number | null;
}

interface RateTrendItem {
  carrier: string;
  lineOfBusiness: string;
  quarter: string;
  avgChangePct: number;
  sampleCount: number;
  indicative: boolean;
}

interface ExtractionFailure {
  documentId: string;
  documentName: string | null;
  customerId: string | null;
  projectId: string | null;
  extractionError: string | null;
  updatedAt: string;
}

interface ExtractionStatus {
  total: number;
  byStatus: Record<string, number>;
  notStarted: number;
  recentFailures: ExtractionFailure[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatLob(lob: string | null): string {
  if (!lob) return '—';
  return lob.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatPremium(p: number | null): string {
  if (p == null) return '—';
  return '$' + p.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatPct(p: number | null): string {
  if (p == null) return '—';
  const sign = p > 0 ? '+' : '';
  return `${sign}${p.toFixed(1)}%`;
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  const parsed = new Date(`${d}T00:00:00`);
  if (isNaN(parsed.getTime())) return d;
  return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// Best-available link into a document's project — there's no direct deep
// link to the chip editor itself, so this lands the user in the project
// that holds it (F9/F11).
function customerProjectLink(customerId: string | null, projectId: string | null): string | null {
  if (customerId && projectId) return `/customers/${customerId}/projects/${projectId}`;
  if (customerId) return `/customers/${customerId}`;
  return null;
}

const LOW_CONFIDENCE_THRESHOLD = 0.7;

// Below this magnitude, treat a change as flat rather than up/down — avoids
// e.g. 0.0% rendering with a colored down-arrow as if rates actually fell.
const NEUTRAL_PCT_THRESHOLD = 0.5;

type TrendDirection = 'up' | 'down' | 'neutral' | null;

function trendDirection(p: number | null): TrendDirection {
  if (p == null) return null;
  if (Math.abs(p) < NEUTRAL_PCT_THRESHOLD) return 'neutral';
  return p > 0 ? 'up' : 'down';
}

function trendTextColor(dir: TrendDirection): string {
  if (dir === 'up') return 'text-red-600 dark:text-red-400';
  if (dir === 'down') return 'text-green-600 dark:text-green-400';
  if (dir === 'neutral') return 'text-gray-500 dark:text-gray-400';
  return 'text-gray-400';
}

function bucketLabel(b: string): string {
  return b === 'expired' ? 'Expired — no renewal uploaded'
    : b === '0-30' ? 'Due in 30 days'
    : b === '31-60' ? 'Due in 31–60 days'
    : b === '61-90' ? 'Due in 61–90 days'
    : 'Due in 91+ days';
}

function bucketColor(b: string): string {
  return b === 'expired' ? 'text-gray-600 dark:text-gray-400'
    : b === '0-30' ? 'text-red-600 dark:text-red-400'
    : b === '31-60' ? 'text-amber-600 dark:text-amber-400'
    : 'text-green-600 dark:text-green-400';
}

function signalTypeLabel(t: string): string {
  const map: Record<string, string> = {
    non_renewal: 'Non-Renewal',
    conditional_renewal: 'Conditional Renewal',
    new_exclusion: 'New Exclusion',
    limit_reduction: 'Limit Reduction',
    deductible_increase: 'Deductible Increase',
    rate_increase: 'Rate Increase',
    capacity_restriction: 'Capacity Restriction',
    declination: 'Declination',
  };
  return map[t] || t;
}

// ─── Section wrapper ──────────────────────────────────────────────────────────

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({
  title, subtitle, children,
}) => (
  <div className="mb-8">
    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{title}</h2>
    {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{subtitle}</p>}
    {children}
  </div>
);

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl p-4 ${className}`}>
    {children}
  </div>
);

const SectionError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <Card>
    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
      <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
      <span>Couldn't load this section.</span>
      <button onClick={onRetry} className="text-[#1993e5] hover:underline flex items-center gap-1">
        <RefreshCw className="w-3 h-3" /> Retry
      </button>
    </div>
  </Card>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const SECTION_COUNT = 7;

// A failed fetch marks its own section failed (and clears stale data so
// SectionError shows) without touching the others — this is what lets
// per-section Retry re-fetch just the one endpoint instead of all 7 (F11).
function markFailed(setFailed: React.Dispatch<React.SetStateAction<string[]>>, name: string, failed: boolean) {
  setFailed((prev) => {
    if (failed) return prev.includes(name) ? prev : [...prev, name];
    return prev.includes(name) ? prev.filter((s) => s !== name) : prev;
  });
}

const Insights: React.FC = () => {
  const navigate = useNavigate();
  const [expirations, setExpirations] = useState<ExpirationsResponse | null>(null);
  const [bookTrends, setBookTrends] = useState<BookTrendsResponse | null>(null);
  const [gaps, setGaps] = useState<GapsResponse | null>(null);
  const [marketSignals, setMarketSignals] = useState<MarketSignal[] | null>(null);
  const [carrierRetention, setCarrierRetention] = useState<CarrierRetention[] | null>(null);
  const [rateTrends, setRateTrends] = useState<RateTrendItem[] | null>(null);
  const [extractionStatus, setExtractionStatus] = useState<ExtractionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [failedSections, setFailedSections] = useState<string[]>([]);
  const [expandedSignals, setExpandedSignals] = useState<Set<string>>(new Set());
  const [retryingDocIds, setRetryingDocIds] = useState<Set<string>>(new Set());
  const [analyzing, setAnalyzing] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchExpirations = useCallback(async () => {
    try {
      setExpirations(await apiClient.get<ExpirationsResponse>('/api/intelligence/expirations?days=90'));
      markFailed(setFailedSections, 'expirations', false);
    } catch { setExpirations(null); markFailed(setFailedSections, 'expirations', true); }
  }, []);
  const fetchBookTrends = useCallback(async () => {
    try {
      const data = await apiClient.get<BookTrendsResponse>('/api/intelligence/book-trends');
      setBookTrends({ bookSummary: data.bookSummary });
      markFailed(setFailedSections, 'bookTrends', false);
    } catch { setBookTrends(null); markFailed(setFailedSections, 'bookTrends', true); }
  }, []);
  const fetchGaps = useCallback(async () => {
    try {
      setGaps(await apiClient.get<GapsResponse>('/api/intelligence/gaps'));
      markFailed(setFailedSections, 'gaps', false);
    } catch { setGaps(null); markFailed(setFailedSections, 'gaps', true); }
  }, []);
  const fetchMarketSignals = useCallback(async () => {
    try {
      setMarketSignals(await apiClient.get<MarketSignal[]>('/api/intelligence/company/market-signals'));
      markFailed(setFailedSections, 'marketSignals', false);
    } catch { setMarketSignals(null); markFailed(setFailedSections, 'marketSignals', true); }
  }, []);
  const fetchCarrierRetention = useCallback(async () => {
    try {
      setCarrierRetention(await apiClient.get<CarrierRetention[]>('/api/intelligence/company/carrier-retention'));
      markFailed(setFailedSections, 'carrierRetention', false);
    } catch { setCarrierRetention(null); markFailed(setFailedSections, 'carrierRetention', true); }
  }, []);
  const fetchRateTrends = useCallback(async () => {
    try {
      setRateTrends(await apiClient.get<RateTrendItem[]>('/api/intelligence/company/rate-trends'));
      markFailed(setFailedSections, 'rateTrends', false);
    } catch { setRateTrends(null); markFailed(setFailedSections, 'rateTrends', true); }
  }, []);
  const fetchStatus = useCallback(async () => {
    try {
      setExtractionStatus(await apiClient.get<ExtractionStatus>('/api/intelligence/status'));
      markFailed(setFailedSections, 'status', false);
    } catch { setExtractionStatus(null); markFailed(setFailedSections, 'status', true); }
  }, []);

  // Refetches every section without blanking the page to a spinner — stale
  // data stays visible until the new data arrives (F11). Only the very
  // first load (see `loading` below) shows the full-page spinner.
  const loadAll = useCallback(async () => {
    setRefreshing(true);
    await Promise.allSettled([
      fetchExpirations(), fetchBookTrends(), fetchGaps(), fetchMarketSignals(),
      fetchCarrierRetention(), fetchRateTrends(), fetchStatus(),
    ]);
    setRefreshing(false);
    setLoading(false);
  }, [fetchExpirations, fetchBookTrends, fetchGaps, fetchMarketSignals, fetchCarrierRetention, fetchRateTrends, fetchStatus]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const retryExtraction = useCallback(async (documentId: string) => {
    setRetryingDocIds((prev) => new Set(prev).add(documentId));
    try {
      await apiClient.post(`/api/intelligence/extract/${documentId}`, {});
      await loadAll();
    } finally {
      setRetryingDocIds((prev) => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
    }
  }, [loadAll]);

  // Stop polling on unmount so a background timer doesn't call setState
  // after the page navigates away.
  useEffect(() => () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
  }, []);

  const analyzeAllDocuments = useCallback(async () => {
    setAnalyzing(true);
    try {
      await apiClient.post('/api/intelligence/extract-all', {});
    } catch {
      setAnalyzing(false);
      return;
    }

    let attempts = 0;
    const MAX_ATTEMPTS = 40; // ~10 minutes at 15s intervals
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(async () => {
      attempts++;
      try {
        const status = await apiClient.get<ExtractionStatus>('/api/intelligence/status');
        setExtractionStatus(status);
        const stillWorking = status.notStarted > 0
          || (status.byStatus?.pending ?? 0) > 0
          || (status.byStatus?.processing ?? 0) > 0;
        if (!stillWorking || attempts >= MAX_ATTEMPTS) {
          if (pollTimerRef.current) clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          setAnalyzing(false);
          await loadAll();
        }
      } catch {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
        setAnalyzing(false);
      }
    }, 15000);
  }, [loadAll]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-[#1993e5]" />
        <span className="ml-2 text-gray-500 dark:text-gray-400">Loading insights…</span>
      </div>
    );
  }

  if (failedSections.length === SECTION_COUNT) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertTriangle className="w-8 h-8 text-red-400 mb-2" />
        <p className="text-gray-700 dark:text-gray-300">Failed to load insights</p>
        <button onClick={loadAll} className="mt-3 text-sm text-[#1993e5] hover:underline flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      </div>
    );
  }

  const sectionFailed = (name: string) => failedSections.includes(name);

  const totalDocs = extractionStatus?.total ?? 0;
  const completedDocs = extractionStatus?.byStatus?.completed ?? 0;
  const notStartedDocs = extractionStatus?.notStarted ?? 0;
  // Pending/processing and not-yet-analyzed are disjoint buckets — don't add
  // notStartedDocs in here too, or it gets counted in both banner lines (F9).
  const pendingDocs = (extractionStatus?.byStatus?.pending ?? 0) + (extractionStatus?.byStatus?.processing ?? 0);
  const failedDocs = extractionStatus?.byStatus?.failed ?? 0;
  const recentFailures = extractionStatus?.recentFailures ?? [];

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Insights</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            AI-extracted insights from your uploaded documents
          </p>
        </div>
        <button
          onClick={loadAll}
          disabled={refreshing}
          className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Extraction coverage banner */}
      {totalDocs > 0 && (
        <div className="mb-6 rounded-lg border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-surface">
          <div className="flex items-center gap-4 flex-wrap text-xs text-gray-500 dark:text-gray-400 px-4 py-2">
            <span>Coverage: <strong className="text-gray-800 dark:text-gray-200">{completedDocs}/{totalDocs}</strong> docs analyzed</span>
            {pendingDocs > 0 && <span className="text-amber-600 dark:text-amber-400">{pendingDocs} pending</span>}
            {failedDocs > 0 && <span className="text-red-600 dark:text-red-400">{failedDocs} failed</span>}
            {notStartedDocs > 0 && (
              <span className="flex items-center gap-2">
                {notStartedDocs} not yet analyzed
                <button
                  onClick={analyzeAllDocuments}
                  disabled={analyzing}
                  className="text-[#1993e5] hover:underline font-medium flex items-center gap-1 disabled:opacity-50"
                >
                  {analyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                  {analyzing ? 'Analyzing…' : 'Analyze my documents'}
                </button>
              </span>
            )}
          </div>
          {recentFailures.length > 0 && (
            <div className="border-t border-gray-200 dark:border-dark-border px-4 py-2 space-y-1.5">
              {recentFailures.map((f) => {
                const link = customerProjectLink(f.customerId, f.projectId);
                const retrying = retryingDocIds.has(f.documentId);
                return (
                  <div key={f.documentId} className="flex items-center gap-2 text-xs">
                    <AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0" />
                    {link ? (
                      <button onClick={() => navigate(link)} className="text-gray-700 dark:text-gray-300 hover:text-[#1993e5] hover:underline font-medium truncate max-w-[220px]">
                        {f.documentName || 'Untitled document'}
                      </button>
                    ) : (
                      <span className="text-gray-700 dark:text-gray-300 font-medium truncate max-w-[220px]">{f.documentName || 'Untitled document'}</span>
                    )}
                    <span className="text-gray-400 truncate flex-1 min-w-0">{f.extractionError || 'Extraction failed'}</span>
                    <button
                      onClick={() => retryExtraction(f.documentId)}
                      disabled={retrying}
                      className="text-[#1993e5] hover:underline flex items-center gap-1 flex-shrink-0 disabled:opacity-50"
                    >
                      <RefreshCw className={`w-3 h-3 ${retrying ? 'animate-spin' : ''}`} /> {retrying ? 'Retrying…' : 'Retry'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── 1. Renewal Pipeline ────────────────────────────────────────────── */}
      <Section
        title="Renewal Pipeline"
        subtitle="Upcoming expirations and recently-expired accounts with no renewal uploaded — one row per customer + line, most recent term only."
      >
        {sectionFailed('expirations') ? (
          <SectionError onRetry={fetchExpirations} />
        ) : (!expirations || expirations.items.length === 0) ? (
          <Card><p className="text-sm text-gray-500 dark:text-gray-400">No upcoming or recently-expired renewals found.</p></Card>
        ) : (
          <div className="space-y-4">
            {(['expired', '0-30', '31-60', '61-90', '91+'] as const).map((bucket) => {
              const items = expirations.buckets[bucket] || [];
              if (items.length === 0) return null;
              return (
                <div key={bucket}>
                  <div className={`text-xs font-semibold uppercase tracking-wide mb-2 ${bucketColor(bucket)}`}>
                    {bucketLabel(bucket)} ({items.length})
                  </div>
                  <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-dark-border">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-dark-bg text-gray-500 dark:text-gray-400 text-xs uppercase">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium">Named Insured</th>
                          <th className="px-4 py-2 text-left font-medium">Customer</th>
                          <th className="px-4 py-2 text-left font-medium">Line</th>
                          <th className="px-4 py-2 text-left font-medium">Carrier</th>
                          <th className="px-4 py-2 text-left font-medium">Expires</th>
                          <th className="px-4 py-2 text-right font-medium">Premium</th>
                          <th className="px-4 py-2 text-right font-medium">Days Out</th>
                          <th className="px-4 py-2 text-right font-medium"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-dark-border bg-white dark:bg-dark-surface">
                        {items.map((item) => {
                          const lowConfidence = item.confidence != null && item.confidence < LOW_CONFIDENCE_THRESHOLD;
                          const reviewLink = customerProjectLink(item.customerId, item.projectId);
                          return (
                            <tr key={item.intelligenceId} className="hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors">
                              <td className="px-4 py-2 font-medium text-gray-900 dark:text-white max-w-[240px] break-words">
                                <div className="flex items-center gap-1.5">
                                  <span>{item.namedInsured || '—'}</span>
                                  {lowConfidence && (
                                    reviewLink ? (
                                      <button
                                        onClick={() => navigate(reviewLink)}
                                        title="Low-confidence extraction — click to review"
                                        className="flex-shrink-0 text-[10px] font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded px-1 py-0.5 hover:underline"
                                      >
                                        low confidence
                                      </button>
                                    ) : (
                                      <span title="Low-confidence extraction" className="flex-shrink-0 text-[10px] font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded px-1 py-0.5">
                                        low confidence
                                      </span>
                                    )
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{item.customerName || '—'}</td>
                              <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{formatLob(item.lineOfBusiness)}</td>
                              <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{item.carrier || '—'}</td>
                              <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{formatDate(item.expirationDate)}</td>
                              <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">{formatPremium(item.totalPremium)}</td>
                              <td className={`px-4 py-2 text-right font-semibold ${bucketColor(bucket)}`}>
                                {item.daysOut < 0 ? `${Math.abs(item.daysOut)}d ago` : `${item.daysOut}d`}
                              </td>
                              <td className="px-4 py-2 text-right">
                                {item.customerId && (
                                  <button
                                    onClick={() => navigate(renewalDeepLink(item.customerId!, item.projectId))}
                                    className="text-[#1993e5] hover:underline font-medium"
                                  >
                                    Start Renewal
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* ── 2. My Book Trends ─────────────────────────────────────────────── */}
      <Section
        title="My Book Trends"
        subtitle="Premium-weighted change by line of business across uploaded renewal terms."
      >
        {sectionFailed('bookTrends') ? (
          <SectionError onRetry={fetchBookTrends} />
        ) : (!bookTrends || bookTrends.bookSummary.length === 0) ? (
          <Card><p className="text-sm text-gray-500 dark:text-gray-400">Not enough renewal data to show trends yet. Upload renewal documents to build your book.</p></Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {[...bookTrends.bookSummary].sort((a, b) => b.termCount - a.termCount).map((row) => {
              const dir = trendDirection(row.avgChangePct);
              return (
                <Card key={row.lineOfBusiness} className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{formatLob(row.lineOfBusiness)}</span>
                  <div className="flex items-center gap-1">
                    {dir === 'up' && <TrendingUp className="w-4 h-4 text-red-500" />}
                    {dir === 'down' && <TrendingDown className="w-4 h-4 text-green-500" />}
                    {dir === 'neutral' && <Minus className="w-4 h-4 text-gray-400" />}
                    <span className={`text-xl font-bold ${trendTextColor(dir)}`}>
                      {formatPct(row.avgChangePct)}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">{row.termCount} term{row.termCount !== 1 ? 's' : ''}</span>
                </Card>
              );
            })}
          </div>
        )}
      </Section>

      {/* ── 3. Opportunities ──────────────────────────────────────────────── */}
      <Section
        title="Opportunities"
        subtitle="Cross-sell gaps and monoline accounts detected from uploaded policies, ranked by account size."
      >
        {sectionFailed('gaps') ? (
          <SectionError onRetry={fetchGaps} />
        ) : (!gaps || gaps.gaps.length === 0) ? (
          <Card><p className="text-sm text-gray-500 dark:text-gray-400">No obvious coverage gaps detected yet.</p></Card>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-dark-border">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-dark-bg text-gray-500 dark:text-gray-400 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Named Insured</th>
                    <th className="px-4 py-2 text-right font-medium">Premium</th>
                    <th className="px-4 py-2 text-left font-medium">Has</th>
                    <th className="px-4 py-2 text-left font-medium">Missing</th>
                    <th className="px-4 py-2 text-left font-medium">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-dark-border bg-white dark:bg-dark-surface">
                  {gaps.gaps.map((g, i) => (
                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors">
                      <td className="px-4 py-2 font-medium text-gray-900 dark:text-white max-w-[240px] break-words">{g.namedInsured || '—'}</td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">{formatPremium(g.accountPremium)}</td>
                      <td className="px-4 py-2 text-gray-600 dark:text-gray-300">
                        {g.presentLines.map((l) => (
                          <span key={l} className="inline-block text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded px-1.5 py-0.5 mr-1 mb-0.5">{formatLob(l)}</span>
                        ))}
                      </td>
                      <td className="px-4 py-2">
                        {g.missingLines.map((l) => (
                          <span key={l} className="inline-block text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 rounded px-1.5 py-0.5 mr-1 mb-0.5">{formatLob(l)}</span>
                        ))}
                      </td>
                      <td className="px-4 py-2 text-gray-500 dark:text-gray-400 text-xs">
                        {g.isMonoline && <span className="text-orange-600 dark:text-orange-400 font-medium">Monoline</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {gaps.totalMatches > gaps.gaps.length && (
              <p className="text-xs text-gray-400 mt-2">
                Showing top {gaps.gaps.length} of {gaps.totalMatches} accounts with gaps.
              </p>
            )}
          </>
        )}
      </Section>

      {/* ── 4. Agency-wide Market Intelligence ───────────────────────────── */}
      <div className="border-t border-gray-200 dark:border-dark-border pt-6 mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Building2 className="w-4 h-4 text-gray-400" />
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Agency-wide</span>
        </div>
        <p className="text-xs text-gray-400 mb-6">Pooled, de-identified patterns from all agents in your agency. Customer names are never surfaced here.</p>
      </div>

      {/* Market Signals */}
      <Section title="Market Signals" subtitle="Carrier signals detected across the agency's uploaded documents.">
        {sectionFailed('marketSignals') ? (
          <SectionError onRetry={fetchMarketSignals} />
        ) : (!marketSignals || marketSignals.length === 0) ? (
          <Card><p className="text-sm text-gray-500 dark:text-gray-400">No market signals detected yet.</p></Card>
        ) : (
          <div className="space-y-2">
            {marketSignals.slice(0, 20).map((sig, i) => {
              const key = `${sig.carrier}|${sig.signalType}|${i}`;
              const expanded = expandedSignals.has(key);
              return (
                <Card key={key} className="!p-0 overflow-hidden">
                  <button
                    type="button"
                    aria-expanded={expanded}
                    className="w-full text-left p-4 flex items-start justify-between gap-2"
                    onClick={() => setExpandedSignals((prev) => {
                      const next = new Set(prev);
                      next.has(key) ? next.delete(key) : next.add(key);
                      return next;
                    })}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Shield className="w-3.5 h-3.5 text-[#1993e5] flex-shrink-0" />
                        <span className="font-medium text-gray-900 dark:text-white text-sm">{sig.carrier || 'Unknown carrier'}</span>
                        <span className="text-xs text-gray-400">·</span>
                        <span className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded px-1.5 py-0.5">{signalTypeLabel(sig.signalType)}</span>
                        {sig.lineOfBusiness && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">{formatLob(sig.lineOfBusiness)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-bg rounded px-2 py-0.5">{sig.count}×</span>
                      {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>
                  </button>
                  {expanded && sig.quotes.length > 0 && (
                    <div className="mx-4 mb-4 space-y-1.5 border-t border-gray-100 dark:border-dark-border pt-3">
                      {sig.quotes.slice(0, 3).map((q, qi) => (
                        <p key={qi} className="text-xs text-gray-500 dark:text-gray-400 italic">"{q}"</p>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
        {marketSignals && marketSignals.length > 20 && (
          <p className="text-xs text-gray-400 mt-2">Showing top 20 of {marketSignals.length} market signals.</p>
        )}
      </Section>

      {/* Carrier Retention */}
      <Section title="Carrier Retention" subtitle="How well each carrier retained agency business (based on uploaded renewals).">
        {sectionFailed('carrierRetention') ? (
          <SectionError onRetry={fetchCarrierRetention} />
        ) : (!carrierRetention || carrierRetention.length === 0) ? (
          <Card><p className="text-sm text-gray-500 dark:text-gray-400">Not enough multi-term data to compute retention yet.</p></Card>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-dark-border">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-dark-bg text-gray-500 dark:text-gray-400 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Carrier</th>
                    <th className="px-4 py-2 text-right font-medium">Renewed</th>
                    <th className="px-4 py-2 text-right font-medium">Moved Away</th>
                    <th className="px-4 py-2 text-right font-medium">Lapsed</th>
                    <th className="px-4 py-2 text-right font-medium">Retention</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-dark-border bg-white dark:bg-dark-surface">
                  {carrierRetention.map((cr) => (
                    <tr key={cr.carrier} className="hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors">
                      <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">{cr.carrier}</td>
                      <td className="px-4 py-2 text-right text-green-600 dark:text-green-400">{cr.renewed}</td>
                      <td className="px-4 py-2 text-right text-amber-600 dark:text-amber-400">{cr.moved}</td>
                      <td className="px-4 py-2 text-right text-gray-400">{cr.lapsed}</td>
                      <td className="px-4 py-2 text-right font-semibold text-gray-900 dark:text-white">
                        {cr.retentionRate != null ? `${Math.round(cr.retentionRate * 100)}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Note: retention is based on uploaded renewal documents. Accounts without an uploaded renewal appear as "lapsed" even if they renewed elsewhere.
            </p>
          </>
        )}
      </Section>

      {/* Rate Trends */}
      <Section title="Rate Trends" subtitle="Average premium change by carrier, line, and quarter across the agency's uploaded documents.">
        {sectionFailed('rateTrends') ? (
          <SectionError onRetry={fetchRateTrends} />
        ) : (!rateTrends || rateTrends.length === 0) ? (
          <Card><p className="text-sm text-gray-500 dark:text-gray-400">Not enough prior-term premium data to show rate trends yet.</p></Card>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-dark-border">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-dark-bg text-gray-500 dark:text-gray-400 text-xs uppercase">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Carrier</th>
                  <th className="px-4 py-2 text-left font-medium">Line</th>
                  <th className="px-4 py-2 text-left font-medium">Quarter</th>
                  <th className="px-4 py-2 text-right font-medium">Avg Change</th>
                  <th className="px-4 py-2 text-right font-medium">Sample Size</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-border bg-white dark:bg-dark-surface">
                {rateTrends.slice(0, 30).map((rt) => {
                  const dir = trendDirection(rt.avgChangePct);
                  return (
                    <tr key={`${rt.carrier}|${rt.lineOfBusiness}|${rt.quarter}`} className="hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors">
                      <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">{rt.carrier}</td>
                      <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{formatLob(rt.lineOfBusiness)}</td>
                      <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{rt.quarter}</td>
                      <td className={`px-4 py-2 text-right font-semibold ${trendTextColor(dir)}`}>
                        {formatPct(rt.avgChangePct)}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">
                        {rt.sampleCount}
                        {rt.indicative && (
                          <span className="ml-1.5 text-[10px] font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded px-1 py-0.5 align-middle">
                            indicative
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {rateTrends && rateTrends.length > 30 && (
          <p className="text-xs text-gray-400 mt-2">Showing top 30 of {rateTrends.length} rate trends.</p>
        )}
      </Section>
    </div>
  );
};

export default Insights;
